import React, { useEffect, useState } from 'react';
import api from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Users, DollarSign, CreditCard, Settings as SettingsIcon, CheckCircle2, BookOpen } from 'lucide-react';
import CourseManager from './admin/CourseManager';
import SplashEditor from './admin/SplashEditor';
import ResourcesEditor from './admin/ResourcesEditor';
import WaitlistViewer from './admin/WaitlistViewer';
import BookManager from './admin/BookManager';
import AboutEditor from './admin/AboutEditor';
import UserManager from './admin/UserManager';
import GameManager from './admin/GameManager';
import LegalEditor from './admin/LegalEditor';
import AICoachEditor from './admin/AICoachEditor';
import EmailSettings from './admin/EmailSettings';
import PagesAndMenu from './admin/PagesAndMenu';
import VideoManager from './admin/VideoManager';
import FeaturesManager from './admin/FeaturesManager';
import RadioManager from './admin/RadioManager';
import AiAssistantLauncher from '@/components/ai/AiAssistantLauncher';

export default function Admin() {
  const { user } = useAuth();
  const [tab, setTab] = useState('overview');
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [payments, setPayments] = useState([]);
  const [settings, setSettings] = useState(null);
  const [savedMsg, setSavedMsg] = useState('');

  const load = async () => {
    const [s, u, p, st] = await Promise.all([
      api.get('/admin/stats').then(r => r.data).catch(() => null),
      api.get('/admin/users').then(r => r.data).catch(() => []),
      api.get('/admin/payments').then(r => r.data).catch(() => []),
      api.get('/settings/payments').then(r => r.data).catch(() => null),
    ]);
    setStats(s); setUsers(u); setPayments(p); setSettings(st);
  };
  useEffect(() => { load(); }, []);

  const verify = async (id) => {
    await api.post(`/admin/payments/${id}/verify`);
    load();
  };

  const saveSettings = async () => {
    await api.put('/admin/settings/payments', settings);
    setSavedMsg('Settings saved.');
    setTimeout(() => setSavedMsg(''), 2500);
    load();
  };

  if (!user?.is_admin) return <div className="kc-container py-16">Admin access required.</div>;

  return (
    <div className="kc-container py-8 md:py-12">
      <div className="flex items-end justify-between gap-3 mb-6">
        <div>
          <div className="text-[11px] tracking-[0.18em] uppercase text-gold">Admin</div>
          <h1 className="font-display uppercase text-4xl md:text-5xl mt-1">Mission control</h1>
        </div>
      </div>

      <div className="flex gap-1 mb-6 overflow-x-auto" data-testid="admin-tabs">
        {[{id:'overview',label:'Overview'},{id:'features',label:'Features'},{id:'splash',label:'Splash'},{id:'pages',label:'Pages & Menu'},{id:'videos',label:'Real Talk'},{id:'radio',label:'Radio'},{id:'about',label:'About'},{id:'courses',label:'Courses'},{id:'games',label:'Games'},{id:'ai_coach',label:'AI Coach'},{id:'bookshelf',label:'Bookshelf'},{id:'resources',label:'Resources'},{id:'legal',label:'Legal'},{id:'waitlist',label:'Waitlist'},{id:'users',label:'Users'},{id:'email',label:'Email'},{id:'payments',label:'Payments'},{id:'settings',label:'Settings'}].map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)} className={`px-4 py-2 text-sm uppercase tracking-wider font-semibold rounded-full whitespace-nowrap ${tab === t.id ? 'bg-ink text-white' : 'text-muted-kc hover:bg-surface-2'} transition-colors`} data-testid={`admin-tab-${t.id}`}>{t.label}</button>
        ))}
      </div>

      {tab === 'courses' && <CourseManager />}
      {tab === 'features' && <FeaturesManager />}
      {tab === 'radio' && <RadioManager />}
      {tab === 'splash' && <SplashEditor />}
      {tab === 'pages' && <PagesAndMenu />}
      {tab === 'videos' && <VideoManager />}
      {tab === 'about' && <AboutEditor />}
      {tab === 'bookshelf' && <BookManager />}
      {tab === 'resources' && <ResourcesEditor />}
      {tab === 'waitlist' && <WaitlistViewer />}
      {tab === 'legal' && <LegalEditor />}
      {tab === 'ai_coach' && <AICoachEditor />}
      {tab === 'email' && <EmailSettings />}
      {tab === 'games' && <GameManager />}

      {tab === 'overview' && stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="admin-stats">
          <Stat icon={Users} label="Total users" value={stats.total_users} />
          <Stat icon={Users} label="Paid users" value={stats.paid_users} />
          <Stat icon={CreditCard} label="Payments" value={stats.total_payments} sub={`${stats.pending_payments} pending`} />
          <Stat icon={DollarSign} label="Revenue" value={`$${(stats.revenue || 0).toFixed(0)}`} />
        </div>
      )}

      {tab === 'users' && <UserManager />}

      {tab === 'payments' && (
        <div className="bg-white border border-kc rounded-[var(--kc-radius-md)] overflow-hidden" data-testid="admin-payments">
          <table className="w-full text-sm">
            <thead className="bg-surface-2 text-left text-xs uppercase tracking-wider">
              <tr><th className="p-3">User</th><th className="p-3">Amount</th><th className="p-3">Method</th><th className="p-3">Status</th><th className="p-3">Date</th><th className="p-3"></th></tr>
            </thead>
            <tbody>
              {payments.map((p) => (
                <tr key={p.id} className="border-t border-kc">
                  <td className="p-3"><div className="font-semibold">{p.user?.name}</div><div className="text-xs text-muted-kc">{p.user?.email}</div></td>
                  <td className="p-3 font-display text-lg text-gold">${p.amount}</td>
                  <td className="p-3 capitalize">{p.method}</td>
                  <td className="p-3">{p.status === 'verified' ? <span className="inline-flex items-center gap-1 text-[color:var(--kc-success)] text-xs font-semibold"><CheckCircle2 size={14}/> Verified</span> : <span className="text-xs font-semibold text-[color:var(--kc-warning)] uppercase tracking-wider">{p.status}</span>}</td>
                  <td className="p-3 text-muted-kc text-xs">{new Date(p.created_at).toLocaleDateString()}</td>
                  <td className="p-3 text-right">{p.status === 'pending' && <button onClick={() => verify(p.id)} className="text-xs font-semibold underline hover:text-gold" data-testid={`verify-${p.id}`}>Verify</button>}</td>
                </tr>
              ))}
              {payments.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-muted-kc text-sm">No payments yet.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'settings' && settings && (
        <div className="max-w-2xl space-y-4" data-testid="admin-settings">
          <div className="bg-white border border-kc rounded-[var(--kc-radius-md)] p-5">
            <h3 className="font-display uppercase text-2xl">Payment methods</h3>
            <p className="text-muted-kc text-sm mt-1">Toggle which methods are shown on the unlock page.</p>
            <div className="mt-4 space-y-3">
              <Toggle label="Venmo enabled" checked={settings.venmo_enabled} onChange={(v) => setSettings({...settings, venmo_enabled: v})} testid="toggle-venmo" />
              <div className="pl-7"><label className="text-xs uppercase tracking-wider text-muted-kc">Venmo handle</label><input value={settings.venmo_handle} onChange={(e) => setSettings({...settings, venmo_handle: e.target.value})} className="mt-1 w-full md:w-72 h-10 px-3 rounded-[var(--kc-radius-sm)] border border-kc-strong" data-testid="input-venmo" /></div>
              <Toggle label="CashApp enabled" checked={settings.cashapp_enabled} onChange={(v) => setSettings({...settings, cashapp_enabled: v})} testid="toggle-cashapp" />
              <div className="pl-7"><label className="text-xs uppercase tracking-wider text-muted-kc">CashApp handle</label><input value={settings.cashapp_handle} onChange={(e) => setSettings({...settings, cashapp_handle: e.target.value})} className="mt-1 w-full md:w-72 h-10 px-3 rounded-[var(--kc-radius-sm)] border border-kc-strong" data-testid="input-cashapp" /></div>
              <Toggle label="Stripe enabled" checked={settings.stripe_enabled} onChange={(v) => setSettings({...settings, stripe_enabled: v})} testid="toggle-stripe" />
              <div className="pl-7"><label className="text-xs uppercase tracking-wider text-muted-kc">Stripe publishable key</label><input value={settings.stripe_publishable_key || ''} onChange={(e) => setSettings({...settings, stripe_publishable_key: e.target.value})} placeholder="pk_live_..." className="mt-1 w-full md:w-96 h-10 px-3 rounded-[var(--kc-radius-sm)] border border-kc-strong" data-testid="input-stripe" /></div>
            </div>
          </div>
          <div className="bg-white border border-kc rounded-[var(--kc-radius-md)] p-5">
            <h3 className="font-display uppercase text-2xl">Suggested tiers</h3>
            <p className="text-muted-kc text-sm mt-1">Comma-separated values that show on the paywall.</p>
            <input value={(settings.suggested_tiers || []).join(',')} onChange={(e) => setSettings({...settings, suggested_tiers: e.target.value.split(',').map(v => parseFloat(v.trim()) || 0)})} className="mt-3 w-full md:w-96 h-10 px-3 rounded-[var(--kc-radius-sm)] border border-kc-strong" data-testid="input-tiers" />
          </div>
          <div className="flex items-center gap-3">
            <button onClick={saveSettings} className="h-11 px-5 rounded-[var(--kc-radius-sm)] bg-[color:var(--kc-gold)] text-black font-semibold hover:bg-[color:var(--kc-gold-2)] transition-colors" data-testid="settings-save">Save settings</button>
            {savedMsg && <span className="text-sm text-[color:var(--kc-success)]">{savedMsg}</span>}
          </div>
        </div>
      )}

      {/* Global AI Design Assistant — available on every tab except Splash (which has its own scoped launcher that can apply edits directly to splash fields). */}
      {tab !== 'splash' && (
        <AiAssistantLauncher
          pageContext={`Admin · ${tab.charAt(0).toUpperCase() + tab.slice(1)} tab`}
          currentData={{}}
        />
      )}
    </div>
  );
}

const Stat = ({ icon: Icon, label, value, sub }) => (
  <div className="bg-white border border-kc rounded-[var(--kc-radius-md)] p-5">
    <div className="flex items-center justify-between">
      <div className="text-[11px] tracking-[0.18em] uppercase text-muted-kc">{label}</div>
      <Icon size={16} className="text-gold"/>
    </div>
    <div className="font-display text-4xl mt-2">{value}</div>
    {sub && <div className="text-xs text-muted-kc mt-1">{sub}</div>}
  </div>
);

const Toggle = ({ label, checked, onChange, testid }) => (
  <button type="button" onClick={() => onChange(!checked)} className="flex items-center gap-3 text-sm font-semibold" data-testid={testid}>
    <span className={`relative w-11 h-6 rounded-full transition-colors ${checked ? 'bg-[color:var(--kc-gold)]' : 'bg-gray-300'}`}>
      <span className={`absolute top-0.5 ${checked ? 'left-6' : 'left-0.5'} w-5 h-5 bg-white rounded-full shadow transition-all`} />
    </span>
    {label}
  </button>
);
